
import React, { useState } from 'react';
import { Rule, ProrationRule } from '../types';
import { DeleteIcon, EditIcon, AddIcon } from './icons';
import ProrationRuleModal from './ProrationRuleModal';

interface RuleCardProps {
    rule: Rule;
    onDelete: (id: string) => void;
    onEdit: (rule: Rule) => void;
}

const RuleCard: React.FC<RuleCardProps> = ({ rule, onDelete, onEdit }) => (
    <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg flex justify-between items-center group">
        <div>
            <span className={`text-xs font-semibold mr-2 px-2.5 py-0.5 rounded bg-blue-200 dark:bg-blue-900 text-blue-800 dark:text-blue-300`}>
                {rule.type}
            </span>
            <p className="text-light-text-primary dark:text-text-primary mt-2">
                Si el concepto incluye <span className="font-semibold text-amber-600 dark:text-amber-300">"{rule.concept}"</span>
            </p>
            <p className="text-sm text-light-text-secondary dark:text-text-secondary">
                Asignar a la categoría: <span className="font-semibold text-primary">{rule.category}</span>
            </p>
        </div>
        <div className="flex space-x-2">
             <button 
                onClick={() => onEdit(rule)} 
                className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-primary"
                title="Editar regla"
            >
                <EditIcon className="w-5 h-5"/>
            </button>
            <button 
                onClick={() => onDelete(rule.id)} 
                className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-red-500"
                title="Eliminar regla"
            >
                <DeleteIcon className="w-5 h-5"/>
            </button>
        </div>
    </div>
);

const ProrationRuleCard: React.FC<{ rule: ProrationRule; onDelete: (id: string) => void; onEdit: (rule: ProrationRule) => void; }> = ({ rule, onDelete, onEdit }) => (
    <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg group">
        <div className="flex justify-between items-start">
            <div>
                <h4 className="font-semibold text-light-text-primary dark:text-text-primary">{rule.name}</h4>
                <p className="text-sm text-light-text-secondary dark:text-text-secondary">
                    Gasto de origen: <span className="font-medium text-amber-600 dark:text-amber-300">{rule.sourceCategory}</span>
                </p>
            </div>
            <div className="flex space-x-2">
                <button onClick={() => onEdit(rule)} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-primary" title="Editar regla"><EditIcon className="w-5 h-5"/></button>
                <button onClick={() => onDelete(rule.id)} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-red-500" title="Eliminar regla"><DeleteIcon className="w-5 h-5"/></button>
            </div>
        </div>
        <div className="mt-3 pt-3 border-t border-light-border dark:border-gray-700">
            <p className="text-xs text-light-text-secondary dark:text-text-secondary mb-2">Se distribuye en:</p>
            <ul className="space-y-1">
                {rule.targets.map((target, index) => (
                    <li key={index} className="flex justify-between text-sm">
                        <span className="text-light-text-primary dark:text-text-primary">{target.category}</span>
                        <span className="font-mono font-medium text-primary">{target.percentage}%</span>
                    </li>
                ))}
            </ul>
        </div>
    </div>
);

interface SettingsViewProps {
    rules: Rule[];
    onDeleteRule: (id: string) => void;
    onOpenRuleModal: (rule: Rule | null) => void;
    prorationRules: ProrationRule[];
    onSaveProrationRule: (ruleData: Omit<ProrationRule, 'id'>, id?: string) => void;
    onDeleteProrationRule: (id: string) => void;
    categories: string[];
    onSaveRule: (ruleData: Omit<Rule, 'id'>, id?: string) => void;
}

const SettingsView: React.FC<SettingsViewProps> = ({ rules, onDeleteRule, onOpenRuleModal, prorationRules, onSaveProrationRule, onDeleteProrationRule, categories, onSaveRule }) => {
    const categorizationRules = rules.filter(r => r.type === 'Categorization');
    const [isProrationModalOpen, setIsProrationModalOpen] = useState(false);
    const [editingProrationRule, setEditingProrationRule] = useState<ProrationRule | null>(null);

    const handleOpenProrationModal = (rule?: ProrationRule) => {
        setEditingProrationRule(rule || null);
        setIsProrationModalOpen(true);
    };

    const handleSaveAndCloseProration = (ruleData: Omit<ProrationRule, 'id'>, id?: string) => {
        onSaveProrationRule(ruleData, id);
        setIsProrationModalOpen(false);
    };

    return (
        <>
        <ProrationRuleModal
            isOpen={isProrationModalOpen}
            onClose={() => setIsProrationModalOpen(false)}
            onSave={handleSaveAndCloseProration}
            categories={categories}
            ruleToEdit={editingProrationRule}
        />
        <div className="space-y-8">
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                 <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="font-bold text-lg text-light-text-primary dark:text-text-primary">Reglas de Categorización Automática</h3>
                        <p className="text-sm text-light-text-secondary dark:text-text-secondary mt-1">
                            Define reglas para que la IA asigne categorías a tus transacciones automáticamente. Puedes editar las que la IA crea o añadir las tuyas.
                        </p>
                    </div>
                    <button onClick={() => onOpenRuleModal(null)} className="flex items-center px-4 py-2 bg-secondary text-white rounded-lg hover:bg-green-600 transition-colors duration-200 text-sm font-medium shadow-sm flex-shrink-0">
                        <AddIcon className="w-5 h-5 mr-2"/> Añadir Regla
                    </button>
                </div>
                <div className="space-y-3">
                    {categorizationRules.length > 0 ? (
                        categorizationRules.map(rule => <RuleCard key={rule.id} rule={rule} onDelete={onDeleteRule} onEdit={onOpenRuleModal} />)
                    ) : (
                        <p className="text-center py-8 text-light-text-secondary dark:text-text-secondary">No hay reglas automáticas. Empieza a categorizar transacciones para crearlas.</p>
                    )}
                </div>
            </div>
            
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="font-bold text-lg text-light-text-primary dark:text-text-primary">Prorrateo de Gastos</h3>
                        <p className="text-sm text-light-text-secondary dark:text-text-secondary">Distribuye gastos compartidos (ej. alquiler, internet) entre varias categorías según un porcentaje.</p>
                    </div>
                     <button onClick={() => handleOpenProrationModal()} className="flex items-center px-4 py-2 bg-secondary text-white rounded-lg hover:bg-green-600 transition-colors duration-200 text-sm font-medium shadow-sm flex-shrink-0">
                        <AddIcon className="w-5 h-5 mr-2"/> Añadir Regla
                     </button>
                </div>
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {prorationRules.length > 0 ? (
                        prorationRules.map(rule => <ProrationRuleCard key={rule.id} rule={rule} onDelete={onDeleteProrationRule} onEdit={handleOpenProrationModal} />)
                    ) : (
                        <p className="col-span-full text-center py-8 text-light-text-secondary dark:text-text-secondary">No has creado ninguna regla de prorrateo.</p>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default SettingsView;
